import { useMutation, useQuery } from '@tanstack/react-query';
import { LOCAL_STORAGE_WORKSPACE_ID } from 'config/constants';
import {
  CONDITION_DEVIATION_BY_ASSET_ID,
  CONDITION_DEVIATION_BY_ID,
  CONDITION_DEVIATION_CATEGORIES_OPTIONS,
  CONDITION_DEVIATION_EQUIPMENTS,
  CONDITION_DEVIATION_LIST,
  CONDITION_DEVIATION_OPTIONS,
  CREATE_CONDITION_DEVIATION,
  EDIT_CONDITION_DEVIATION,
  EVENTS,
} from 'config/endpoints';
import { ActionStatus } from 'queries/assets/contract';
import { EventFilters, EventItemPayload, FilterOptionsResponse } from 'queries/events/types';
import { EDateReference } from 'queries/tags';
import { GenericQueryOptions } from 'queries/types/users-and-permissions';
import { get, post, put } from 'utils/axios';
import {
  ConditionDeviation,
  ConditionDeviationsList,
  CreateConditionDeviationPayload,
  EditConditionDeviationPayload,
  EquipmentQuery,
  ListData,
} from './contract';
import { useConditionDeviationsListProps } from './types';

export const useConditionDeviationListQuery = ({
  actionStatus,
  filters,
  includeDescendant,
  enableFilterDate = true,
  queryOptions,
}: useConditionDeviationsListProps) => {
  if (actionStatus === 'ALL') actionStatus = null;
  const actionStatusString =
    actionStatus === 'ACTION_ISSUED' ? 'ACTION_ISSUED,INVESTIGATING' : actionStatus;

  const { assets, category, end_date, start_date, date_reference } = filters;
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    [
      'CONDITION_DEVIATION',
      'CD_LIST',
      actionStatus,
      category,
      assets,
      date_reference,
      enableFilterDate,
      start_date,
      end_date,
    ],
    get<
      ConditionDeviationsList,
      {
        status: ActionStatus | null | string;
        date_reference?: EDateReference.START | EDateReference.END;
        start_date?: string;
        end_date?: string | null;
        asset_categories?: string;
        ancestor_asset_ids?: string;
        include_descendant_events?: boolean;
      }
    >(CONDITION_DEVIATION_LIST(workspaceId), {
      status: actionStatusString,
      asset_categories: filters?.category?.join(','),
      ancestor_asset_ids: filters?.assets?.map((asset) => asset.id)?.join(','),
      date_reference: date_reference ? EDateReference.END : EDateReference.START,
      start_date: enableFilterDate ? start_date : undefined,
      end_date: enableFilterDate ? end_date : undefined,
      include_descendant_events: includeDescendant ?? true,
    }),
    { retry: false, enabled: workspaceId !== null, ...queryOptions },
  );
};

export const useConditionDeviationListByIdQuery = (
  { assetId, month, year }: { assetId: string; month?: string | number; year?: string | number },
  queryOptions?: GenericQueryOptions<ConditionDeviation[]>,
) =>
  useQuery(
    ['CD_LOADING', 'CONDITION_DEVIATION', 'CD_LIST_BY_ASSET_ID', assetId, month, year],
    async () => {
      const response = await get<
        ConditionDeviationsList,
        { month?: string | number; year?: string | number }
      >(CONDITION_DEVIATION_BY_ASSET_ID(assetId), { month, year })();
      return response.condition_deviations;
    },

    { enabled: !!assetId, retry: false, ...queryOptions },
  );

export const useConditionDeviationById = (params: {
  id?: string;
  return_linked_alarms?: boolean;
}) => {
  const { id, return_linked_alarms } = params;

  return useQuery({
    queryKey: ['CONDITION_DEVIATION_BY_ID', id, return_linked_alarms],
    queryFn: get<ConditionDeviation, unknown>(CONDITION_DEVIATION_BY_ID(id), {
      return_linked_alarms,
    }),
    enabled: !!id,
  });
};

export const useCreateConditionDeviation = (
  onSuccess: (cdId: string, variables: CreateConditionDeviationPayload) => void,
  onError?: () => void,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useMutation(
    (data: CreateConditionDeviationPayload) => {
      const payload = { ...data, category: undefined };
      delete payload['attachments-CD_EVENT'];
      delete payload['attachments-CD_SAVINGS'];
      return post<CreateConditionDeviationPayload, { id: string }>(CREATE_CONDITION_DEVIATION, {
        payload: { ...payload, workspace_id: workspaceId },
      });
    },
    {
      onSuccess: (response, variables) => {
        onSuccess(response.data?.id, variables);
      },
      onError,
    },
  );
};

export const useUpdateConditionDeviation = (
  id: string,
  onSuccess: (id: string, variables: EditConditionDeviationPayload) => void,
  onError?: () => void,
) =>
  useMutation(
    (cdData: EditConditionDeviationPayload) => {
      const payload = { ...cdData, category: undefined };
      delete payload['attachments-CD_EVENT'];
      delete payload['attachments-CD_SAVINGS'];

      delete payload.workspace_id;

      return put<EditConditionDeviationPayload>(EDIT_CONDITION_DEVIATION(id), { payload });
    },
    {
      onSuccess: (_data, variables) => onSuccess(id, variables),
      onError,
    },
  );

export const useConditionDeviationOptionsQuery = (queryOptions?: GenericQueryOptions<ListData>) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(['CD_OPTIONS_ALL'], get<ListData>(CONDITION_DEVIATION_OPTIONS(workspaceId)), {
    enabled: workspaceId !== null,
    ...queryOptions,
  });
};

export const useConditionDeviationEquipmentsQuery = (
  queryOptions?: GenericQueryOptions<EquipmentQuery>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    ['CD_EQUIPMENTS_ALL'],
    get<EquipmentQuery>(CONDITION_DEVIATION_EQUIPMENTS(workspaceId)),
    { enabled: workspaceId !== null, ...queryOptions },
  );
};
export const useConditionDeviationCategoriesOptionsQuery = (
  queryOptions?: GenericQueryOptions<FilterOptionsResponse>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    ['CD_CATEGORIES_OPTIONS'],
    get<FilterOptionsResponse>(CONDITION_DEVIATION_CATEGORIES_OPTIONS(workspaceId)),
    {
      enabled: workspaceId !== null,
      ...queryOptions,
    },
  );
};

export const useConditionDeviationEventsQuery = (
  { filters }: { filters: EventFilters },
  queryOptions?: GenericQueryOptions<EventItemPayload>,
) => {
  const { assets, category, end_date, start_date, status, alarms_without_cds } = filters;
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;

  return useQuery(
    [
      'CONDITION_DEVIATION',
      'EVENTS',
      assets,
      category,
      status,
      start_date,
      end_date,
      alarms_without_cds,
    ],
    get(EVENTS(workspaceId), {
      start_date,
      end_date,
      status,
      event_categories: filters?.category?.join(','),
      ancestor_asset_ids: filters?.assets?.map((asset) => asset.id)?.join(','),
      alarms_without_cds,
    }),
    { retry: false, enabled: workspaceId !== null, ...queryOptions },
  );
};
